/**
 * Onboarding: Welcome → name + avatar → secure account → welcome-gift popup → home.
 * Registration happens once (step "signup"); the bonus is granted server-side.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { registerSchema, type AuthResponse, type GameTypeDto } from '@cardhub/protocol';
import { api } from '../api/client';
import { GoogleSignIn } from '../components/GoogleSignIn';
import { GuestButton } from '../components/GuestButton';
import { Avatar, CoinIcon, Confetti, CountUp, Icon, Modal } from '../components/ui';
import { GameArt } from '../cards/PlayingCard';
import { useT } from '../i18n';
import { errorMessage } from '../lib/hooks';
import { deviceId } from '../lib/device';
import { sfx } from '../lib/sound';
import { saveSettings, useSession } from '../store/session';
import { useSettings } from '../store/settings';

const AVATARS = Array.from({ length: 24 }, (_, i) => `avatar_${String(i + 1).padStart(2, '0')}`);
const GAMES: { type: GameTypeDto; cards: string[] }[] = [
  { type: 'CALL_BREAK', cards: ['AS', 'KS', 'QS'] },
  { type: 'SPADES', cards: ['AS', 'AH', 'KS'] },
  { type: 'TARNEEB', cards: ['AH', 'KH', 'QH'] },
  { type: 'HEARTS', cards: ['QS', 'AH', 'KH'] },
  { type: 'FIVE_HUNDRED', cards: ['JK:0', 'JS', 'AS'] },
  { type: 'JUDGEMENT', cards: ['AH', 'KH', '5C'] },
  { type: 'RUMMY', cards: ['7H', '8H', 'JK:0'] },
  { type: 'EUCHRE', cards: ['JS', 'JC', 'AS'] },
  { type: 'GIN_RUMMY', cards: ['7S', '8S', '9S'] },
  { type: 'TWENTY_NINE', cards: ['JH', '9H', 'AH'] },
  { type: 'CRAZY_EIGHTS', cards: ['8S', '8H', '8D'] },
  { type: 'CRIBBAGE', cards: ['5H', '5D', '5S'] },
  { type: 'BLACKJACK', cards: ['AS', 'KH'] },
  { type: 'BHABHI', cards: ['AS', 'KS', 'QS'] },
  { type: 'PINOCHLE', cards: ['QS', 'JD', 'AH'] },
  { type: 'ANDAR_BAHAR', cards: ['7H', '7S', '7D'] },
  { type: 'BRIDGE', cards: ['AS', 'KS', 'QS'] },
  { type: 'SEVENS', cards: ['6H', '7H', '8H'] },
  { type: 'SCOPA', cards: ['7D', 'KD', 'AD'] },
  { type: 'BACCARAT', cards: ['9S', 'KH'] },
  { type: 'BLUFF', cards: ['AS', 'AH', 'AD'] },
  { type: 'DRAGON_TIGER', cards: ['KS', 'QH'] },
  { type: 'FLUSH', cards: ['AH', 'KH', 'QH'] },
  { type: 'COURT_PIECE', cards: ['AS', 'KS', 'JS'] },
  { type: 'DEHLA_PAKAD', cards: ['TS', 'TH', 'TD'] },
  { type: 'WHIST', cards: ['AS', 'QS', 'JS'] },
  { type: 'SEEP', cards: ['9C', 'TD', 'KH'] },
  { type: 'BELOTE', cards: ['JS', '9S', 'AS'] },
  { type: 'DURAK', cards: ['6S', '9H', 'AD'] },
  { type: 'PRESIDENT', cards: ['2S', 'AH', 'KD'] },
  { type: 'BRISCOLA', cards: ['AS', '3H', 'KD'] },
  { type: 'CASINO_WAR', cards: ['KS', 'KH'] },
  { type: 'THREE_CARD_POKER', cards: ['AS', 'KH', 'QD'] },
  { type: 'GO_FISH', cards: ['7S', '7H', '7D'] },
  { type: 'TRESSETTE', cards: ['3S', '2H', 'AD'] },
  { type: 'RED_DOG', cards: ['2S', 'KH'] },
  { type: 'CANASTA', cards: ['2S', 'JK:0', 'AH'] },
  { type: 'SKAT', cards: ['JC', 'JS', 'AD'] },
  { type: 'PAI_GOW', cards: ['AS', 'KH', 'QD'] },
  { type: 'BIG_TWO', cards: ['2S', '2H', 'AD'] },
  { type: 'WAR', cards: ['KS', 'QH'] },
  { type: 'RUMMY_500', cards: ['7S', '8S', '9S'] },
  { type: 'BRAG', cards: ['AH', 'AD', 'AC'] },
  { type: 'PITCH', cards: ['AS', 'JS', 'TS'] },
  { type: 'HAZARI', cards: ['AS', 'KS', 'QS'] },
  { type: 'TEEN_PATTI', cards: ['AH', 'AD', 'AC'] },
  { type: 'POKER', cards: ['AS', 'KS'] },
  { type: 'MARRIAGE', cards: ['7H', '8H', '9H'] },
];
type Step = 'welcome' | 'profile' | 'signup';
const STEPS: Step[] = ['welcome', 'profile', 'signup'];

export function Welcome() {
  const t = useT();
  const nav = useNavigate();
  const params = useParams();
  const [search] = useSearchParams();
  const next = search.get('next') ?? '/';
  const applyAuth = useSession((s) => s.applyAuth);
  const status = useSession((s) => s.status);
  const lang = useSettings((s) => s.settings.language);
  const [step, setStep] = useState<Step>('welcome');
  const [username, setUsername] = useState('');
  const [avatar, setAvatar] = useState(AVATARS[Math.floor(Math.random() * 8)]!);
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [referral, setReferral] = useState(params.code ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bonus, setBonus] = useState<AuthResponse['signupBonus'] | null>(null);
  const [showBonus, setShowBonus] = useState(false);
  
  const usernameError = useMemo(() => {
    if (!username) return null;
    const r = registerSchema.shape.username.safeParse(username);
    return r.success ? null : t('onboard.username.hint');
  }, [username, t]);

  const [nameTaken, setNameTaken] = useState(false);
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    setNameTaken(false);
    if (!username || usernameError) return;
    setChecking(true);
    let stale = false;
    const h = setTimeout(() => {
      api.get<{ available: boolean }>(`/api/v1/auth/username-available?username=${encodeURIComponent(username)}`)
        .then((r) => { if (!stale) setNameTaken(!r.available); })
        .catch(() => undefined)
        .finally(() => { if (!stale) setChecking(false); });
    }, 350);
    return () => { stale = true; clearTimeout(h); setChecking(false); };
  }, [username, usernameError]);

  useEffect(() => {
    if (status === 'authed' && step === 'welcome' && !showBonus) nav(next, { replace: true });
  }, [status, step, showBonus, nav, next]);

  const register = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<AuthResponse>('/api/v1/auth/register', {
        username,
        password,
        avatar,
        ...(email ? { email } : {}),
        ...(referral ? { referralCode: referral.toUpperCase() } : {}),
        deviceId: deviceId(),
        language: lang,
      });
      applyAuth(res);
      setBonus(res.signupBonus ?? null);
      sfx.coin();
      setShowBonus(true);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setShowBonus(false);
    await api.patch('/api/v1/me', { onboardingCompleted: true }).catch(() => undefined);
    nav(next, { replace: true });
  };

  const idx = STEPS.indexOf(step);
  return (
    <div className="onboard">
      {step !== 'welcome' && (
        <div className="steps" aria-label={`Step ${idx} of ${STEPS.length - 1}`}>
          {STEPS.slice(1).map((s, i) => (
            <span key={s} className={i < idx ? 'on' : ''} />
          ))}
        </div>
      )}

      {step === 'welcome' && (
        <div className="col grow" style={{ justifyContent: 'space-between' }}>
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <select className="input" style={{ width: 'auto', minHeight: 40 }} value={lang} aria-label={t('settings.language')} onChange={(e) => void saveSettings({ language: e.target.value as 'en' | 'ne' | 'hi' })}>
              <option value="en">English</option>
              <option value="ne">नेपाली</option>
              <option value="hi">हिन्दी</option>
            </select>
          </div>
          <div className="col welcome-hero">
            <div className="welcome-fan" aria-hidden>
              <GameArt cards={['AS', 'KH', 'QD', 'JC']} />
            </div>
            <h1>{t('onboard.welcome.title')}</h1>
            <p className="dim" style={{ fontSize: 'var(--fs-lg)', margin: 0 }}>{t('onboard.welcome.body')}</p>
            <div className="row wrap welcome-perks">
              <span className="pill">🎮 {t('onboard.welcome.games', { n: GAMES.length })}</span>
              <span className="pill">🎁 {t('onboard.welcome.perkCoins')}</span>
              <span className="pill">🤖 {t('onboard.welcome.perkBots')}</span>
            </div>
          </div>
          <div className="welcome-marquees" aria-label={t('onboard.welcome.games', { n: GAMES.length })}>
            {[GAMES.slice(0, Math.ceil(GAMES.length / 2)), GAMES.slice(Math.ceil(GAMES.length / 2))].map((row, ri) => (
              <div key={ri} className={`welcome-marquee ${ri ? 'rev' : ''}`}>
                <div className="welcome-track">
                  {[...row, ...row].map((g, i) => (
                    <div key={`${g.type}-${i}`} className="game-card welcome-tile" data-game={g.type} aria-hidden={i >= row.length}>
                      <GameArt cards={g.cards.slice(0, 2)} />
                      <h3>{t(`game.${g.type}`)}</h3>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="col">
            <button className="btn primary lg block" onClick={() => setStep('profile')}>{t('onboard.welcome.cta')}</button>
            <GoogleSignIn
              referralCode={referral || undefined}
              onSignedIn={(res) => {
                applyAuth(res);
                setBonus(res.signupBonus ?? null);
                if (res.signupBonus) setShowBonus(true);
                else void finish();
              }}
            />
            <GuestButton
              onSignedIn={(res) => {
                applyAuth(res);
                setBonus(res.signupBonus ?? null);
                if (res.signupBonus) setShowBonus(true);
                else void finish();
              }}
            />
            <Link className="btn ghost block" to={`/login${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`}>{t('onboard.welcome.login')}</Link>
          </div>
        </div>
      )}

      {step === 'profile' && (
        <form className="col grow" onSubmit={(e) => { e.preventDefault(); if (username && !usernameError && !nameTaken && !checking) setStep('signup'); }}>
          <h1>{t('onboard.username.title')}</h1>
          <div className="row" style={{ alignItems: 'center' }}>
            <Avatar avatar={avatar} size={64} name={username} />
            <div className="field grow" style={{ margin: 0 }}>
              <label htmlFor="un">{t('auth.username')}</label>
              <input id="un" className="input" autoFocus autoComplete="username" value={username} maxLength={20} onChange={(e) => setUsername(e.target.value.replace(/\s/g, ''))} aria-invalid={Boolean(usernameError) || nameTaken} />
              {usernameError ? <span className="error">{usernameError}</span> : nameTaken ? <span className="error">That username is taken</span> : <span className="hint">{t('onboard.username.hint')}</span>}
            </div>
          </div>
          <h3 style={{ margin: '8px 0 0' }}>{t('onboard.avatar.title')}</h3>
          <div className="avatar-grid">
            {AVATARS.map((a) => (
              <button type="button" key={a} aria-pressed={a === avatar} onClick={() => setAvatar(a)} aria-label={a}>
                <Avatar avatar={a} size={56} />
              </button>
            ))}
          </div>
          <div className="grow" />
          <button className="btn primary lg block" disabled={!username || Boolean(usernameError) || nameTaken || checking}>{t('common.continue')}</button>
          <button type="button" className="btn ghost block" onClick={() => setStep('welcome')}>{t('common.back')}</button>
          <Link className="btn ghost block" to={`/login${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`}>{t('onboard.welcome.login')}</Link>
        </form>
      )}

      {step === 'signup' && (
        <form className="col grow" onSubmit={(e) => { e.preventDefault(); void register(); }}>
          <h1>{t('onboard.signup.title')}</h1>
          <p className="dim" style={{ margin: 0 }}>{t('onboard.signup.body')}</p>
          <div className="field">
            <label htmlFor="pw">{t('auth.password')}</label>
            <input id="pw" type="password" className="input" autoComplete="new-password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
            <span className="hint">8+ characters</span>
          </div>
          <div className="field">
            <label htmlFor="em">{t('auth.email')}</label>
            <input id="em" type="email" className="input" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="rf">{t('auth.referral')}</label>
            <input id="rf" className="input" value={referral} maxLength={12} onChange={(e) => setReferral(e.target.value.toUpperCase())} />
          </div>
          {error && <p className="error" role="alert" style={{ color: 'var(--danger)', margin: 0 }}>{error}</p>}
          <div className="grow" />
          <button className="btn primary lg block" disabled={busy || password.length < 8}>{busy ? t('common.loading') : t('auth.register')}</button>
          <button type="button" className="btn ghost block" onClick={() => setStep('profile')}>{t('common.back')}</button>
          <Link className="btn ghost block" to={`/login${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`}>{t('onboard.welcome.login')}</Link>
        </form>
      )}

      <Modal open={showBonus} onClose={() => void finish()} dismissable={false}>
        <div className="col" style={{ alignItems: 'center', textAlign: 'center' }}>
          {bonus?.status === 'GRANTED' && <Confetti />}
          <Icon name="gift" size={56} />
          <h2>{t('onboard.bonus.title')}</h2>
          {bonus?.status === 'GRANTED' ? (
            <>
              <div className="bonus-burst row" style={{ justifyContent: 'center' }}>
                <CoinIcon size={56} /> <CountUp to={bonus.amount} duration={1400} />
              </div>
              <p className="dim">{t('onboard.bonus.body', { amount: new Intl.NumberFormat().format(bonus.amount) })}</p>
            </>
          ) : (
            <p className="dim">{t('onboard.bonus.pending')}</p>
          )}
          <button className="btn primary lg block" onClick={() => void finish()}>{t('common.continue')}</button>
        </div>
      </Modal>
    </div>
  );
}
